import { Check, ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { OnboardingLabels } from "./types";

export interface OnboardingSuccessStateProps {
  labels: OnboardingLabels;
  email?: string;
  onContinue?: () => void;
  onNextStep?: (index: number) => void;
  className?: string;
}

export function OnboardingSuccessState({
  labels,
  email,
  onContinue,
  onNextStep,
  className,
}: OnboardingSuccessStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center text-center",
        "animate-in fade-in-0 slide-in-from-bottom-2 duration-200",
        "motion-reduce:animate-none",
        className,
      )}
    >
      <div
        className={cn(
          "mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-biz-success/10 text-biz-success",
          "animate-in zoom-in-50 duration-200",
          "motion-reduce:animate-none",
        )}
      >
        <Check size={40} weight="bold" aria-hidden />
      </div>

      <h2 className="font-display text-2xl font-bold tracking-tight text-biz-foreground sm:text-3xl">
        {labels.success.title}
      </h2>
      <p className="mt-2 max-w-md text-sm text-biz-foreground-muted sm:text-base">
        {labels.success.subtitle}
      </p>

      {email && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-biz-border bg-biz-surface px-3 py-1.5 text-xs font-medium text-biz-foreground">
          <EnvelopeSimple size={14} className="text-biz-primary" aria-hidden />
          <span className="font-mono">{email}</span>
        </div>
      )}

      {onContinue && (
        <button
          type="button"
          onClick={onContinue}
          className="mt-8 inline-flex h-16 min-w-[14rem] items-center justify-center gap-2 rounded-md bg-biz-primary px-6 text-base font-semibold text-biz-primary-foreground shadow-biz-md transition-all hover:bg-biz-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2 active:scale-[0.98]"
        >
          {labels.success.continueToDashboard}
          <ArrowRight size={20} aria-hidden />
        </button>
      )}

      <div className="mt-12 grid w-full gap-3 sm:grid-cols-3">
        {labels.success.nextSteps.map((step, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onNextStep?.(i)}
            className="group flex h-full flex-col items-start gap-2 rounded-biz-card border border-biz-border bg-biz-surface p-4 text-left transition-all hover:-translate-y-0.5 hover:border-biz-primary/40 hover:shadow-biz-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2 motion-reduce:hover:translate-y-0"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-biz-primary/10 text-xs font-bold text-biz-primary">
              {i + 1}
            </span>
            <p className="font-display text-sm font-semibold text-biz-foreground">{step.title}</p>
            <p className="text-xs text-biz-foreground-muted">{step.description}</p>
            <span className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-biz-primary">
              {step.cta}
              <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" aria-hidden />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
